/**
 * One thread — a root message and its replies.
 *
 * The same channel, filtered to `parentId`. It is a separate component rather than a
 * mode of {@link ChannelView} because almost nothing about the conversation view applies:
 * a thread has no unread cursor of its own, no prompt bar, and its root message is pinned
 * to the top instead of being one row among many. It does read on sight
 * (`markReadOnView`): the channel's one cursor, as a partial view — see
 * `computeReadOnViewTarget`. What the two DO share — the bubble, the
 * day grouping, the edit/delete/pin trio and the composer — they share by using the same
 * components, not by growing a flag.
 *
 * ```tsx
 * <ChannelThreadView cid={cid} parent={{ id: 8213 }} config={{ ... }} />
 * ```
 *
 * ## Finding the root
 *
 * There is no by-id message route, so the root is located inside the channel's own
 * paginated thread (the query the conversation view already fills, so this is usually a
 * cache hit) and the search pages backwards while it is missing. That is also what makes
 * a legacy `?thread-id=<oldId>` link work without an id-mapping endpoint.
 *
 * ## Known limits
 *
 * Replies are one page (25, newest-last). A thread longer than that shows its most
 * recent 25 replies — the same window the legacy office thread rendered.
 */

import { useGetAuthMe } from "@birdwatch/client";
import { cn } from "cn";
import { useMemo, useRef } from "react";
import { Typography } from "@/components/ui/typography";
import { useRealtimeRefresh } from "@/features/shared/realtime-refresh";
import useTimezone from "@/hooks/use-timezone";
import { DEFAULT_TIME_ZONE } from "@/libs/date-fns";
import { useChannel } from "./_api/use-channel";
import {
  useChannelMessages,
  useChannelThreadReplies,
} from "./_api/use-channel-messages";
import { useChannelUnread, useMarkChannelRead } from "./_api/use-channel-read";
import { ChannelComposer } from "./_components/channel-composer";
import { ChannelMessageDeleteDialog } from "./_components/channel-message-delete-dialog";
import { ChannelMessageEdit } from "./_components/channel-message-edit";
import { ChannelMessageItem } from "./_components/channel-message-item";
import { ChannelMessageList } from "./_components/channel-message-list";
import { ChannelMessageSkeleton } from "./_components/channel-message-skeleton";
import { ChannelMessageUnpinDialog } from "./_components/channel-message-unpin-dialog";
import { ChannelScrollToBottom } from "./_components/channel-scroll-to-bottom";
import { ChannelTyping } from "./_components/channel-typing";
import { useChannelMessageInteractions } from "./_hooks/use-channel-message-interactions";
import { useMarkReadOnView } from "./_hooks/use-mark-read-on-view";
import { useQuotedReply } from "./_hooks/use-quoted-reply";
import { useResolvedMessageTarget } from "./_hooks/use-resolved-message-target";
import { scrollChannelToBottom } from "./_libs/channel-scroll";
import type { ChannelMessageTarget } from "./_libs/message-target";
import { getNewestMessageId } from "./_libs/newest-message-id";
import { computeReadOnViewTarget } from "./_libs/read-on-view-target";
import {
  type ChannelDaySeparatorRenderer,
  type ChannelMessageRenderer,
  type ChannelSurfaceConfig,
  resolveChannelSurface,
} from "./_types/channel-surface";

export type ChannelThreadViewProps = {
  cid: string;
  /** The root message: a `Message.id`, or a legacy `oldId` from a deep link. */
  parent: ChannelMessageTarget | null;
  /** Same shape as `ChannelView`'s; `unreadDots`, `readReceipts` and prompt keys are ignored here. */
  config?: ChannelSurfaceConfig;
  /** Scroll to and flash one reply. */
  focusedMessage?: ChannelMessageTarget | null;
  /**
   * Swaps the message bubble (and its day heading) for another UI without touching data,
   * scrolling, pagination, drafts, realtime or the composer. Omitted, this renders exactly
   * what it rendered before the seam existed — see `@/features/shared/conversation`.
   */
  renderMessage?: ChannelMessageRenderer;
  renderDaySeparator?: ChannelDaySeparatorRenderer;
};

export function ChannelThreadView({
  cid,
  parent,
  config,
  focusedMessage,
  renderMessage,
  renderDaySeparator,
}: ChannelThreadViewProps) {
  const { data: me } = useGetAuthMe().get();
  const { timeZone } = useTimezone();
  const containerRef = useRef<HTMLDivElement>(null);
  // The thread ROOT is built here rather than by the list, so it needs the same swap —
  // without this the root keeps the old skin while its replies get the new one.
  const RootMessageItem = renderMessage ?? ChannelMessageItem;

  const { data: channel } = useChannel(cid);
  const surface = useMemo(
    () => resolveChannelSurface(config, channel?.features),
    [config, channel?.features],
  );

  // Live, on the entity the conversation hangs off — see `ChannelView`.
  useRealtimeRefresh({
    channels: [channel?.entityId],
    enabled: surface.realtime,
  });

  const {
    data: messages,
    isLoading: isLoadingChannel,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useChannelMessages(cid, { pageSize: surface.pageSize });

  const rootMessage = useResolvedMessageTarget({
    target: parent,
    messages,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const rootMessageId = rootMessage?.id ?? null;

  const { data: replies, isLoading: isLoadingReplies } =
    useChannelThreadReplies(cid, rootMessageId);

  const interactions = useChannelMessageInteractions(cid);

  const { data: unread } = useChannelUnread(cid, surface.markReadOnView);
  const { markRead } = useMarkChannelRead(cid);
  useMarkReadOnView({
    enabled: surface.markReadOnView && rootMessage != null && !isLoadingReplies,
    target: computeReadOnViewTarget({
      coversWholeChannel: false,
      newestMessageId: getNewestMessageId(replies) ?? rootMessageId,
      lastReadMessageId: unread
        ? (unread.lastReadMessageId ?? null)
        : undefined,
      unreadCount: unread?.unreadCount,
    }),
    delayMs: surface.markReadDelayMs,
    markRead,
  });

  // "Quote reply" inside the thread: the quote belongs to THIS thread, so opening another one in
  // place starts clean, and a quote card jumps among the replies.
  const {
    quotedMessage,
    changeQuotedMessage,
    replyToMessage,
    openQuotedMessage,
    target,
  } = useQuotedReply({
    scopeKey: `${cid}#reply:${rootMessageId}`,
    focusedMessage,
  });

  const focusedReply = useResolvedMessageTarget({
    target,
    messages: replies,
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  });

  if (!channel) {
    return (
      <div
        data-test="channel-thread-view"
        className={cn(
          /* sorted */
          "flex flex-col flex-1",
          "overflow-hidden",
          surface.className,
        )}
      >
        {/* Same band as the message column this skeleton stands in for, so the placeholder
            does not start full width and jump to the capped width once the channel lands. */}
        <div className={cn("w-full mx-auto", surface.contentClassName)}>
          <ChannelMessageSkeleton />
        </div>
      </div>
    );
  }

  const isLoading =
    isLoadingChannel || (rootMessage != null && isLoadingReplies);

  // Same floor as the main view, plus the root: no composer until the thread's root has
  // resolved. Reply writes into the composer, so it is offered exactly when one is there.
  const hasComposer =
    surface.composer !== false &&
    rootMessageId != null &&
    channel.acceptsMessages;

  return (
    <div
      data-test="channel-thread-view"
      data-channel-cid={cid}
      className={cn("flex flex-col flex-1 min-h-0", surface.className)}
    >
      {surface.header}

      <ChannelMessageList
        cid={cid}
        channel={channel}
        messages={replies}
        containerRef={containerRef}
        timeZone={timeZone || DEFAULT_TIME_ZONE}
        viewerId={me?.id}
        viewerRoles={me?.roles}
        surface={surface}
        isLoading={isLoading}
        isFetchingNextPage={false}
        focusedMessageId={focusedReply?.id ?? null}
        focusNonce={target?.nonce ?? 0}
        editingMessageId={interactions.editingMessageId}
        onCancelEdit={interactions.cancelEdit}
        onEditMessage={interactions.startEdit}
        editingQuote={interactions.editingQuote}
        onEditingQuoteChange={interactions.changeEditingQuote}
        onDeleteMessage={interactions.requestDelete}
        onTogglePin={interactions.togglePin}
        // An open comment editor takes the quote; otherwise Reply starts one in the composer.
        onReplyMessage={
          hasComposer
            ? (message) => {
                if (!interactions.quoteIntoEdit(message))
                  replyToMessage(message);
              }
            : undefined
        }
        onOpenQuotedMessage={openQuotedMessage}
        renderMessage={renderMessage}
        renderDaySeparator={renderDaySeparator}
        topSlot={
          rootMessage && (
            <div>
              {interactions.editingMessageId === rootMessage.id ? (
                <ChannelMessageEdit
                  cid={cid}
                  message={rootMessage}
                  onDone={interactions.cancelEdit}
                  surface={surface}
                  quote={interactions.editingQuote}
                  onQuoteChange={interactions.changeEditingQuote}
                />
              ) : (
                <RootMessageItem
                  cid={cid}
                  channel={channel}
                  message={rootMessage}
                  viewerId={me?.id}
                  isUnread={false}
                  showReadReceipts={false}
                  showPinnedBadge={surface.pins}
                  // The parent is context for the replies below it, not a message to act
                  // on here: no reaction cluster, no hover actions. Both live on it in the
                  // thread it was posted in, which is where a reaction belongs.
                  showActions={false}
                  actionsConfig={surface.messageActions}
                  renderBlock={surface.renderBlock}
                  renderAvatar={surface.renderAvatar}
                  renderUserName={surface.renderUserName}
                  onEdit={interactions.startEdit}
                  onDelete={interactions.requestDelete}
                  onTogglePin={interactions.togglePin}
                />
              )}
              <div className="flex items-center gap-2 px-6 h-11">
                <Typography size="sm" className="text-text-dark-low">
                  {/* Server count — exact past the 25-reply page the list loads. */}
                  {rootMessage.replyCount}{" "}
                  {rootMessage.replyCount === 1 ? "reply" : "replies"}
                </Typography>
                <div className="flex-1 border-t border-border-dark-low" />
              </div>
            </div>
          )
        }
      />

      {/* Same gutter pair, and the same full-width-box-with-the-band-inside shape, as
          the main view — see `channel-view.tsx`. */}
      {surface.footer ? (
        <div className="w-full scrollbar-gutter-match">
          <div className={cn("w-full mx-auto", surface.contentClassName)}>
            {surface.footer}
          </div>
        </div>
      ) : null}

      <div className="relative shrink-0 w-full">
        {/* The floating layer (jump-to-newest, typing, the surface's `floating`), outside
            the gutter box's clipping — see the comment on the same layer in
            `channel-view.tsx`. */}
        <div
          className={cn(
            /* sorted */
            "absolute inset-x-0 top-0",
            "w-full",
            "mx-auto",
            surface.contentClassName,
          )}
        >
          <ChannelScrollToBottom containerRef={containerRef} />
          {surface.typing && surface.realtime && rootMessageId != null && (
            <ChannelTyping
              key={`${cid}:${rootMessageId}`}
              cid={cid}
              channel={channel}
              className="absolute -top-3 right-0 left-0 px-3 xl:px-4 pt-1 bg-bg-light"
            />
          )}
          {surface.floating}
        </div>
        {/* Same floor as the main view: the surface may withhold the composer, and a
            channel that refuses the write withholds it regardless. */}
        {!hasComposer ? null : (
          <div className="w-full scrollbar-gutter-match">
            <div
              className={cn(
                /* sorted */
                "flex",
                "flex-col",
                "w-full",
                "p-3 mx-auto",
                "xl:p-4",
                surface.contentClassName,
              )}
            >
              <ChannelComposer
                // Remount per draft key — the editor reads its draft once per mount, so
                // opening a second thread in place would inherit the first reply's text.
                key={`${cid}#reply:${rootMessageId}`}
                cid={cid}
                surface={surface}
                draftKey={`${cid}#reply:${rootMessageId}`}
                replyToId={rootMessageId}
                quotedMessage={quotedMessage}
                onQuotedMessageChange={changeQuotedMessage}
                onSent={() => scrollChannelToBottom(containerRef.current)}
              />
            </div>
          </div>
        )}
      </div>

      <ChannelMessageDeleteDialog
        message={interactions.messagePendingDelete}
        open={interactions.messagePendingDelete != null}
        onOpenChange={(open) => {
          if (!open) interactions.dismissDelete();
        }}
        isDeleting={interactions.isDeleting}
        onConfirm={interactions.deleteMessage}
        deleteConfirm={surface.messageActions.deleteConfirm}
      />

      <ChannelMessageUnpinDialog
        message={interactions.messagePendingUnpin}
        open={interactions.messagePendingUnpin != null}
        onOpenChange={(open) => {
          if (!open) interactions.dismissUnpin();
        }}
        isUnpinning={interactions.isUnpinning}
        onConfirm={interactions.unpinMessage}
        unpinConfirm={surface.messageActions.unpinConfirm}
      />
    </div>
  );
}
